import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { F0Box, F0Heading, F0Text, TwoColumnLayout } from '@factorialco/f0-react'
import { F0TableOfContent, type TOCItem } from '@factorialco/f0-react/dist/experimental'
import {
  ProcurementTypeCardFragment,
  ProcurementTypesDataCollectionDocument,
  RestoreProcurementTypeDocument,
  ToggleProcurementTypeEnablementDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import CreateRequestTypeModal from 'modules/procurement/components/procurementTypes/CreateTypeModal'
import DeleteRequestTypeModal from 'modules/procurement/components/procurementTypes/DeleteRequestTypeModal'
import DuplicateRequestTypeModal from 'modules/procurement/components/procurementTypes/DuplicateTypeModal'
import EditRequestTypeModal from 'modules/procurement/components/procurementTypes/EditRequestTypeModal'
import PoTemplatesContent from 'modules/procurement/pages/PoTemplatesPage'
import TaskFormsContent from 'modules/procurement/pages/TaskFormsPage'

import ProcurementTypesDataCollection from './procurementTypesDataCollection'

type Section = 'requests' | 'forms' | 'po-templates'

type Props = {
  canReadTaskForms: boolean
  canReadPoTemplates: boolean
  canManageTaskForms: boolean
}

const NewProcurementSettingsContent = ({
  canReadTaskForms,
  canReadPoTemplates,
  canManageTaskForms,
}: Props) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const activeSection: Section = pathname.startsWith('/settings/procurement/po-templates')
    ? 'po-templates'
    : pathname.startsWith('/settings/procurement/task-forms')
      ? 'forms'
      : 'requests'

  const [createProcurementTypeModalOpen, setCreateProcurementTypeModalOpen] = useState(false)
  const [editProcurementTypeModalOpen, setEditProcurementTypeModalOpen] = useState(false)
  const [deleteProcurementTypeModalOpen, setDeleteProcurementTypeModalOpen] = useState(false)
  const [duplicateProcurementTypeModalOpen, setDuplicateProcurementTypeModalOpen] = useState(false)
  const [selectedProcurementType, setSelectedProcurementType] =
    useState<ProcurementTypeCardFragment | null>(null)

  const handleShowEditProcurementTypeModal = (procurementType: ProcurementTypeCardFragment) => {
    setSelectedProcurementType(procurementType)
    setEditProcurementTypeModalOpen(true)
  }

  const handleShowDeleteProcurementTypeModal = (procurementType: ProcurementTypeCardFragment) => {
    setSelectedProcurementType(procurementType)
    setDeleteProcurementTypeModalOpen(true)
  }

  const handleShowDuplicateProcurementTypeModal = (
    procurementType: ProcurementTypeCardFragment
  ) => {
    setSelectedProcurementType(procurementType)
    setDuplicateProcurementTypeModalOpen(true)
  }

  const { company } = useCurrentSession()

  const [toggleProcurementTypeEnablement] = useMutation(ToggleProcurementTypeEnablementDocument, {
    extractorFn: (data) => data.procurementMutations.updateProcurementType,
  })
  const [restoreProcurementType] = useMutation(RestoreProcurementTypeDocument, {
    extractorFn: (data) => data.procurementMutations.restoreProcurementType,
  })

  const handleToggleProcurementType = async (procurementType: ProcurementTypeCardFragment) => {
    const result = await toggleProcurementTypeEnablement({
      variables: {
        id: procurementType.id,
        companyId: company.id,
        enabled: !procurementType.enabled,
      },
      refetchQueries: [ProcurementTypesDataCollectionDocument],
    })

    const handled = handleResult(result, () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    )
    return handled
  }

  const handleRestoreProcurementType = async (procurementType: ProcurementTypeCardFragment) => {
    const result = await restoreProcurementType({
      variables: {
        id: procurementType.id,
        companyId: company.id,
      },
      refetchQueries: [ProcurementTypesDataCollectionDocument],
    })

    const handled = handleResult(result, (errors) => {
      const errorMessage = Object.values(errors).filter(Boolean).join(', ')
      app.setFlash({
        type: 'wrong',
        message: errorMessage || i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.purchase_requests.settings.types.actions_new.restore_success'),
      })
    }

    return handled
  }
  const sidebarItems: TOCItem[] = useMemo(
    () => [
      {
        id: 'requests',
        label: i18n.t('procurement.purchase_requests.settings.sidebar.request_types'),
        onClick: () => navigate(navigation.settings.procurement.requests.fullPath()),
      },
      ...(canReadTaskForms
        ? [
            {
              id: 'forms',
              label: i18n.t('procurement.purchase_requests.settings.sidebar.forms'),
              onClick: () => navigate(navigation.settings.procurement.taskForms.fullPath()),
            },
          ]
        : []),
      ...(canReadPoTemplates
        ? [
            {
              id: 'po-templates',
              label: i18n.t('procurement.purchase_requests.settings.sidebar.orders'),
              onClick: () => navigate(navigation.settings.procurement.poTemplates.fullPath()),
            },
          ]
        : []),
    ],
    [navigate, navigation, canReadTaskForms, canReadPoTemplates]
  )

  const procurementTypesContent = (
    <>
      <F0Box width="full" paddingBottom="xl">
        <F0Heading
          content={i18n.t('procurement.purchase_requests.settings.types.title_new')}
          variant="heading-large"
          as="h2"
        />
        <F0Text
          content={i18n.t('procurement.purchase_requests.settings.types.description_new')}
          variant="description"
        />
      </F0Box>

      <ProcurementTypesDataCollection
        onCreateProcurementType={() => setCreateProcurementTypeModalOpen(true)}
        onEditProcurementType={handleShowEditProcurementTypeModal}
        onToggleProcurementType={handleToggleProcurementType}
        onDeleteProcurementType={handleShowDeleteProcurementTypeModal}
        onDuplicateProcurementType={handleShowDuplicateProcurementTypeModal}
        onRestoreProcurementType={handleRestoreProcurementType}
      />
    </>
  )

  const mainContent =
    activeSection === 'po-templates' && canReadPoTemplates ? (
      <>
        <F0Box width="full" paddingBottom="xl">
          <F0Heading
            content={i18n.t('procurement.purchase_requests.settings.po_templates.title')}
            variant="heading-large"
            as="h2"
          />
          <F0Text
            content={i18n.t('procurement.purchase_requests.settings.po_templates.description')}
            variant="description"
          ></F0Text>
        </F0Box>
        <PoTemplatesContent />
      </>
    ) : activeSection === 'forms' && canReadTaskForms ? (
      <>
        <F0Box width="full" paddingBottom="xl">
          <F0Heading
            content={i18n.t('procurement.purchase_requests.settings.task_forms.title')}
            variant="heading-large"
            as="h2"
          />
          <F0Text
            content={i18n.t('procurement.purchase_requests.settings.task_forms.description')}
            variant="description"
          />
        </F0Box>
        <TaskFormsContent canManage={canManageTaskForms} />
      </>
    ) : (
      procurementTypesContent
    )

  return (
    <>
      {canReadTaskForms || canReadPoTemplates ? (
        <F0Box border="none" height="full">
          <TwoColumnLayout
            mainColumnPosition="right"
            sideContent={
              <F0TableOfContent
                title={i18n.t('procurement.purchase_requests.settings.sidebar.purchase')}
                items={sidebarItems}
                activeItem={activeSection}
              />
            }
          >
            {mainContent}
          </TwoColumnLayout>
        </F0Box>
      ) : (
        procurementTypesContent
      )}
      {createProcurementTypeModalOpen && (
        <CreateRequestTypeModal
          onClose={() => setCreateProcurementTypeModalOpen(false)}
          hasFormsEnabled
        />
      )}
      {duplicateProcurementTypeModalOpen && selectedProcurementType && (
        <DuplicateRequestTypeModal
          onClose={() => setDuplicateProcurementTypeModalOpen(false)}
          procurementType={selectedProcurementType}
        />
      )}
      {editProcurementTypeModalOpen && selectedProcurementType && (
        <EditRequestTypeModal
          requestType={selectedProcurementType}
          onClose={() => setEditProcurementTypeModalOpen(false)}
        />
      )}
      {deleteProcurementTypeModalOpen && (
        <DeleteRequestTypeModal
          onClose={() => setDeleteProcurementTypeModalOpen(false)}
          requestType={selectedProcurementType}
        />
      )}
    </>
  )
}

export default NewProcurementSettingsContent
